import { cn } from "@/utils";
import { ChevronDown } from "lucide-react";
import {
  useState,
  type ElementType,
  type ReactNode,
  type SyntheticEvent,
} from "react";

/**
 * Item que abre e fecha: a pergunta à vista, a resposta embaixo.
 *
 * É `<details>`/`<summary>` nativo de propósito. A resposta fechada **continua
 * no HTML** — o crawler indexa, o LLM cita, o Ctrl+F do navegador abre o item
 * sozinho e o leitor de tela anuncia "expandido/recolhido" sem uma linha de
 * ARIA. Um acordeão de `div` com `hidden` some do HTML do prerender, e FAQ que
 * o Google não lê é FAQ que não existe (ele exige a resposta visível na
 * página, não só no JSON-LD).
 *
 * Funciona sem JavaScript: abrir e fechar é do navegador. O estado em React só
 * existe para avisar a tela (`onOpenChange`).
 */

type DisclosureHeadingLevel = 2 | 3 | 4 | 5 | 6;

export interface DisclosureProps {
  /** O que fica sempre à vista: a pergunta, o título do bloco. */
  summary: ReactNode;
  /** O conteúdo que abre. Fica no HTML mesmo fechado. */
  children: ReactNode;
  /** Começa aberto. Depois disso quem manda é o usuário. */
  defaultOpen?: boolean;
  /**
   * Envolve o `summary` num `<h2>`…`<h6>`. Use em FAQ: cada pergunta vira um
   * título na estrutura da página, que é o que o buscador e o leitor de tela
   * usam para navegar. Escolha o nível que continua a hierarquia da seção —
   * pergunta dentro de uma seção `h2` é `3`. Sem ele o summary é texto comum.
   */
  headingLevel?: DisclosureHeadingLevel;
  /** `id` do `<details>` — serve de âncora (`/pagina#prazo-de-cobranca`). */
  id?: string;
  /** Avisado a cada abrir/fechar, com o estado novo. */
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export const Disclosure = ({
  summary,
  children,
  defaultOpen = false,
  headingLevel,
  id,
  onOpenChange,
  className,
}: DisclosureProps) => {
  const [open, setOpen] = useState(defaultOpen);

  // O navegador já abriu ou fechou; aqui só se copia o estado dele. Lendo do
  // elemento (e não invertendo o anterior), abrir pelo Ctrl+F também conta.
  const handleToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
    const next = event.currentTarget.open;
    if (next === open) return;
    setOpen(next);
    onOpenChange?.(next);
  };

  const Title: ElementType = headingLevel ? `h${headingLevel}` : "span";

  return (
    <details
      id={id}
      open={open}
      onToggle={handleToggle}
      className={cn("group", className)}
    >
      <summary
        className={cn(
          // `list-none` + o pseudo-elemento do WebKit tiram o triângulo nativo:
          // o chevron é o único indicador, do mesmo lado em todo navegador.
          "flex min-h-12 cursor-pointer list-none items-center gap-3 py-3 [&::-webkit-details-marker]:hidden",
          "font-ds-sans text-ds-body text-ds-ink-900 font-semibold",
          "rounded-ds-md ease-ds-out transition-colors duration-150 motion-reduce:transition-none",
          "hover:text-ds-brand-700 active:text-ds-brand-800",
          "focus-visible:ring-ds-focus focus-visible:ring-offset-ds-paper focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        )}
      >
        {/* O preflight do Tailwind zera tamanho e peso do h*: o título herda a
            tipografia do summary e a semântica fica só na tag. */}
        <Title className="min-w-0 flex-1 text-pretty">{summary}</Title>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "text-ds-ink-500 size-5 shrink-0",
            "ease-ds-out transition-transform duration-180 motion-reduce:transition-none",
            "group-open:rotate-180"
          )}
        />
      </summary>
      <div className="font-ds-sans text-ds-body text-ds-ink-700 pb-4">
        {children}
      </div>
    </details>
  );
};

export interface DisclosureGroupProps {
  children: ReactNode;
  /**
   * Nome do grupo para o leitor de tela ("Perguntas frequentes"). Sem ele o
   * grupo é só espaçamento — o que basta quando há um título visível logo
   * acima.
   */
  label?: string;
  className?: string;
}

/**
 * Pilha de `Disclosure` com divisor entre os itens e filete em cima e
 * embaixo. Só cuida do espaço: cada item continua abrindo sozinho, e abrir um
 * **não** fecha o outro — quem compara duas respostas precisa das duas à
 * vista.
 */
export const DisclosureGroup = ({
  children,
  label,
  className,
}: DisclosureGroupProps) => (
  <div
    role={label ? "group" : undefined}
    aria-label={label}
    className={cn(
      "border-ds-border divide-ds-border flex flex-col divide-y border-y",
      className
    )}
  >
    {children}
  </div>
);
